import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import { generateCategoryCode } from '../../shared/categoryCode';
import type { CategoryFormValues, SettingsCategory, CategoryType, SubCategoryFormValue } from '../types/categoriesEdit.types';

type Row = Record<string, any>;

const typeLabelMap: Record<string, string> = {
  work: 'Công việc',
  material: 'Vật tư',
  equipment: 'Thiết bị',
  personnel: 'Nhân sự',
  service: 'Dịch vụ',
  other: 'Khác',
};

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

async function getGroupId(typeCode: string, companyId: string | null): Promise<string | null> {
  let query = supabase.from('category_groups').select('id').eq('code', typeCode);
  if (companyId) {
    query = query.or(`company_id.eq.${companyId},company_id.is.null`);
  } else {
    query = query.is('company_id', null);
  }
  const { data } = await query.maybeSingle();
  return data?.id || null;
}

function mapServiceCategory(row: Row): SettingsCategory {
  let groupCode = (row.group?.code || 'other') as CategoryType;
  
  // Fallback: Determine type from code prefix if group is missing
  if (groupCode === 'other' && row.code) {
    const code = String(row.code).toUpperCase();
    if (code.startsWith('W-')) groupCode = 'work';
    else if (code.startsWith('M-')) groupCode = 'material';
    else if (code.startsWith('E-')) groupCode = 'equipment';
    else if (code.startsWith('P-')) groupCode = 'personnel';
    else if (code.startsWith('S-')) groupCode = 'service';
  }

  const iconConfig = row.icon_config || {};

  return {
    id: row.id,
    groupId: row.group_id,
    code: row.code || '',
    name: row.name || '',
    description: row.description || '',
    type: groupCode,
    typeName: row.group?.name || typeLabelMap[groupCode] || 'Khác',
    itemCount: 0,
    status: row.is_active ? 'active' : 'inactive',
    icon: iconConfig.name || 'ri-folder-line',
    tone: iconConfig.tone || 'bg-gray-100 text-gray-600',
    parentId: row.parent_id || null,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || row.created_at || null,
  };
}

async function upsertChildren(parentId: string, groupId: string, children: SubCategoryFormValue[], companyId: string) {
  for (const [index, child] of children.entries()) {
    if (!child.name.trim()) continue;

    let childId = child.id;
    const payload: Record<string, any> = {
      name: child.name.trim(),
      code: child.code.trim() || generateCategoryCode(child.name),
      description: child.description?.trim() || '',
      group_id: groupId,
      is_active: true,
      icon_config: { name: 'ri-folder-line', tone: 'bg-gray-100 text-gray-600' },
      parent_id: parentId,
      display_order: index + 1,
      company_id: companyId,
    };

    if (isUuid(child.id)) {
      const { error } = await supabase.from('categories').update(payload).eq('id', child.id);
      if (error) throw error;
    } else {
      const { data, error } = await supabase.from('categories').insert(payload).select('id').single();
      if (error) throw error;
      childId = String(data.id);
    }

    for (const [grandIndex, grand] of (child.subSubCategories || []).entries()) {
      if (!grand.name.trim()) continue;
      const grandPayload = {
        name: grand.name.trim(),
        code: grand.code.trim() || generateCategoryCode(grand.name),
        description: grand.description?.trim() || '',
        group_id: groupId,
        is_active: true,
        icon_config: { name: 'ri-folder-line', tone: 'bg-gray-100 text-gray-600' },
        parent_id: childId,
        display_order: grandIndex + 1,
        company_id: companyId,
      };

      if (isUuid(grand.id)) {
        const { error } = await supabase.from('categories').update(grandPayload).eq('id', grand.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('categories').insert(grandPayload);
        if (error) throw error;
      }
    }
  }
}

export const categoriesEditService = {
  async getCategoryDetail(id: string): Promise<SettingsCategory | null> {
    const { data, error } = await supabase
      .from('categories')
      .select('*, group:category_groups(code, name)')
      .eq('id', id)
      .maybeSingle();

    if (error || !data) return null;
    return mapServiceCategory(data);
  },

  async getAllCategories(): Promise<SettingsCategory[]> {
    const { data, error } = await supabase
      .from('categories')
      .select('*, group:category_groups(code, name)')
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) return [];
    return (data || []).map(mapServiceCategory);
  },

  async updateCategory(id: string, values: CategoryFormValues, userId: string, companyId: string): Promise<void> {
    await permissionEngine.assert(userId, 'settings.categories.manage', { companyId });
    const groupId = await getGroupId(values.type, companyId);
    
    const payload: Record<string, unknown> = {
      name: values.name?.trim(),
      code: values.code?.trim() || generateCategoryCode(values.name || ''),
      description: values.description?.trim() || '',
      icon_config: {
        name: values.icon?.trim() || 'ri-folder-line',
        tone: 'bg-emerald-100 text-emerald-700',
      },
      group_id: groupId,
      is_active: values.status === 'active',
      company_id: companyId,
    };

    const { error } = await supabase.from('categories').update(payload).eq('id', id);
    if (error) throw error;

    if (groupId) {
      await upsertChildren(id, groupId, values.subCategories || [], companyId);
    }
  },
};
